import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Checkbox } from "semantic-ui-react";
import { getTextByValueAndKey } from "../../api/keyvalue";
import Grid from "../Grid";
import LoadingSpinner from "../LoadingSpinner";
import Button from "../input/Button";
import Dropdown from "../input/Dropdown";
import Input from "../input/Input";
import ButtonRenderer from "../renderer/ButtonRenderer";
import { ResizableScrollingTextRenderer } from "../renderer/StyleRenderer";
import { operationDataValidation } from "../validations/stakeholderCreation";
import { useSelector } from "react-redux";
import { emptyValidator } from "../../validators";
import { AUTH_TYPES, FORMS, MODULES } from "../../const";
import { isNumber } from "../../helper";
import DeleteButtonRenderer from "../renderer/DeleteButtonRenderer";

const FORM = FORMS.STAKEHOLDER.OPERATIONS.form;

const StakeholderOperationType = forwardRef(
  ({ dropdown, isLoading, currentValues, editMode }, ref) => {
    const [operations, setOperations] = useState([]);
    const [optCount, setOptCount] = useState(2);
    const [currentErrors, setCurrentErrors] = useState({});
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [currentOperation, setCurrentOperation] = useState({});
    const [deletedList, setDeletedList] = useState([]);

    const [buttonClicked, setButtonClicked] = useState(false);
    const [mFields, setMFields] = useState({});

    const [stakeholderOperationData, setStakeholderOperationData] = useState({
      spyType: null,
      spyTypeValue: "",
      spyDescription: "",
      spyPrimary: false,
      spyPrimaryValue: "No",
      spyStatus: 2,
      spyActive: 1,
      spyActiveValue: "",
    });

    const mandatoryFields = useSelector(
      (state) => state.stakeholder.operationFormFields,
    );

    useEffect(() => {
      if (Object.entries(mandatoryFields).length <= 0)
        setMFields({
          spyType: {
            field: "spyType",
            name: "Operation Type",
            required: true,
          },
          spyActive: {
            field: "spyActive",
            name: "Active",
            required: true,
          },
        });
      else setMFields(mandatoryFields);
    }, [mandatoryFields]);

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentOperations = [];
          let id = 2;
          for (let currentValue of currentValues) {
            currentOperations.push({
              ...currentValue,
              spyStakeholder: currentValue.spyStakeholderId,
              spyPrimaryValue: currentValue.spyPrimary ? "Yes" : "No",
              id,
            });
            ++id;
          }
          setOptCount(id);
          setOperations(currentOperations);
        }
      })();
    }, [editMode, currentValues]);

    useEffect(() => {
      if (buttonClicked) {
        setStakeholderOperationData({
          spyType: null,
          spyTypeValue: "",
          spyDescription: "",
          spyPrimary: false,
          spyPrimaryValue: "No",
          spyActive: null,
          spyActiveValue: "",
          spyStatus: 1,
        });
        setCurrentOperation({});
        setButtonClicked(false);
        setCurrentOperation({});
      }
    }, [buttonClicked]);

    useEffect(() => {
      if (Object.keys(currentOperation).length > 0) {
        setStakeholderOperationData({
          ...currentOperation,
          spyTypeValue: "",
          spyActiveValue: "",
        });
      }
    }, [editMode, currentOperation]);

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          const updateState = {
            modified: [],
            created: [],
            deleted: deletedList,
          };

          operations.forEach((spy) => {
            if (spy.spyId && spy.spyId !== null) {
              if (spy.modified) {
                updateState.modified.push(spy);
              }
            } else {
              updateState.created.push(spy);
            }
          });

          return updateState;
        } else {
          return operations;
        }
      },
      resetForm: () => {
        setButtonClicked(true);
        setOperations([]);
      },
    }));

    const onAddClick = async () => {
      setIsAddLoading(true);

      let validationState = {};

      for (const [key, value] of Object.entries(mFields)) {
        validationState[key] = {
          validators: [emptyValidator()],
          name: value.name,
        };
      }

      let optErrors = operationDataValidation(
        {
          ...stakeholderOperationData,
        },
        validationState,
      );

      if (Object.keys(optErrors).length === 0) {
        const spyTypeValue = await getTextByValueAndKey(
          "vpy_type",
          stakeholderOperationData.spyType,
        );
        const spyActiveValue = await getTextByValueAndKey(
          "active",
          stakeholderOperationData.spyActive,
        );
        const spyPrimaryValue = stakeholderOperationData.spyPrimary
          ? "Yes"
          : "No";
        if (stakeholderOperationData.spyPrimary === true) {
          const primaryOperations = operations.filter((spy) => spy.spyPrimary);
          if (
            primaryOperations.length >= 1 &&
            primaryOperations[0].id !== stakeholderOperationData.id
          ) {
            optErrors = {
              ...optErrors,
              primaryCountError:
                "Can't have more than one primary Operation type",
            };
            setCurrentErrors(optErrors);
            setIsAddLoading(false);
            return;
          }
        }
        if (
          !stakeholderOperationData.spyId ||
          stakeholderOperationData.spyId === null
        ) {
          if (
            !stakeholderOperationData.id ||
            stakeholderOperationData.id === null
          ) {
            setOperations((prevState) => [
              ...prevState,
              {
                ...stakeholderOperationData,
                id: optCount,
                spyTypeValue,
                spyActiveValue,
                spyPrimaryValue,
              },
            ]);

            setOptCount((prevState) => ++prevState);
          } else {
            setOperations((prevState) =>
              prevState.filter(
                (item) => item.id !== stakeholderOperationData.id,
              ),
            );
            setOperations((prevState) => [
              ...prevState,
              {
                ...stakeholderOperationData,
                spyTypeValue,
                spyActiveValue,
                spyPrimaryValue,
              },
            ]);
          }
        } else {
          setOperations((prevState) =>
            prevState.filter(
              (item) => item.spyId !== stakeholderOperationData.spyId,
            ),
          );
          setOperations((prevState) => [
            ...prevState,
            {
              ...stakeholderOperationData,
              spyTypeValue,
              spyActiveValue,
              spyPrimaryValue,
              modified: true,
            },
          ]);
        }
        setButtonClicked(true);
      }

      setCurrentErrors(optErrors);
      setIsAddLoading(false);
    };

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <div className="ui dividing header">Operation Type</div>
              <div className="two fields">
                <Dropdown
                  required={mFields.spyType?.required}
                  label="Operation Type"
                  error={currentErrors.spyType}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="spy_type"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { spyType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Operation Type"
                  options={dropdown.frcgApparelOptions}
                  style={{ height: "20px" }}
                  value={stakeholderOperationData.spyType}
                  onChange={(e, data) => {
                    setStakeholderOperationData((prevState) => ({
                      ...prevState,
                      spyType: data.value,
                    }));
                  }}
                />

                <div
                  className={`field ${currentErrors.spyPrimary ? "error" : ""}`}
                >
                  <label>Primary</label>
                  <Checkbox
                    type="Checkbox"
                    name="primary"
                    checked={stakeholderOperationData.spyPrimary}
                    value={stakeholderOperationData.spyPrimary}
                    onChange={(e) => {
                      setStakeholderOperationData((prevState) => ({
                        ...prevState,
                        spyPrimary: !prevState.spyPrimary,
                      }));
                    }}
                    editMode={editMode}
                    module={MODULES.Master}
                    form={FORM}
                    field="spy_primary"
                  />
                  <small style={{ color: "#9f3a38" }}>{`${
                    currentErrors.spyPrimary ? currentErrors.spyPrimary : ""
                  }`}</small>
                </div>
              </div>

              <div className="two fields">
                <Input
                  label="Description"
                  error={currentErrors.spyDescription}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="spy_description"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { spyDescription: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="description"
                  placeholder="Description"
                  value={stakeholderOperationData.spyDescription}
                  onChange={(e) => {
                    setStakeholderOperationData((prevState) => ({
                      ...prevState,
                      spyDescription: e.target.value,
                    }));
                  }}
                />

                <Dropdown
                  label="Active"
                  required={mFields.spyActive?.required}
                  error={currentErrors.spyActive}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="spy_active"
                  needDeletePermission={true}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { spyActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Active"
                  options={dropdown.activeOptions}
                  style={{ height: "20px" }}
                  value={stakeholderOperationData.spyActive}
                  onChange={(e, data) => {
                    setStakeholderOperationData((prevState) => ({
                      ...prevState,
                      spyActive: data.value,
                    }));
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  template="reset"
                  onClick={(e) => {
                    setButtonClicked(true);
                  }}
                />
                <Button
                  template="addOrSave"
                  authenticated={true}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={`${FORM}`}
                  create={Object.keys(currentOperation).length === 0}
                  onClick={onAddClick}
                  isLoading={isAddLoading}
                />
              </div>
              <small style={{ color: "#9f3a38" }}>{`${
                currentErrors.primaryCountError
                  ? currentErrors.primaryCountError
                  : ""
              }`}</small>
              <Grid
                heading="Stakeholder Operations"
                config={[
                  { headerName: "Type", field: "spyTypeValue" },
                  {
                    headerName: "Description",
                    field: "spyDescription",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  { headerName: "Primary", field: "spyPrimaryValue" },
                  { headerName: "Active", field: "spyActiveValue" },
                  {
                    field: "Edit",
                    filter: true,
                    cellRenderer: ButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.DELETE,
                      clickHandler: () => {
                        setCurrentOperation(params.data);
                      },
                      label: "Edit",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode: editMode,
                    }),
                  },
                  {
                    field: "Delete",
                    filter: true,
                    cellRenderer: DeleteButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.CREATE,
                      clickHandler: () => {
                        if (params.data.spyId && params.data.spyId !== null) {
                          setDeletedList((prevState) => [
                            ...prevState,
                            params.data.spyId,
                          ]);
                        }
                        setOperations((prevoperations) =>
                          prevoperations.filter(
                            (operation) => operation.id !== params.data.id,
                          ),
                        );
                      },
                      message:
                          "Are you sure you want to delete this Stakeholder Operation Type?",
                      label: "Delete",
                      className: "ui red button",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode:
                        params.data.spyId !== null &&
                        isNumber(params.data.spyId),
                    }),
                  },
                ]}
                content={operations}
              />
            </div>
          </form>
        )}
      </>
    );
  },
);

export default StakeholderOperationType;
